import React, { useState, useMemo, useEffect } from 'react';
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/audit/adapters';
import { AuditLog } from '../services/api/domains/audit/types';
import { useAuditLogsPage } from '../services/api/domains/audit/hooks';
import TableSearch from '../components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from '../components/TableSearch/types';
import { auditLogsFieldConfig } from '../config/searchConfigs';
import LgmDataGrid from '../components/LgmDataGrid';
import {
  gridFilterToAdvancedQuery,
  mergeAdvancedQueries,
} from '../utils/dataGridHelpers';

const PAGE_SIZE = 50;

const AuditLogs: React.FC = () => {
  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });

  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'changed_date', sort: 'desc' },
  ]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const auditLogsParams = useMemo(() => {
    let finalAdvancedQuery = createEmptyAdvancedQuery();

    // Check if grid filters are present
    const hasGridFilters = gridFilterModel.items.length > 0;

    // Start with TableSearch query (basic or advanced)
    if (searchMode === 'advanced') {
      finalAdvancedQuery = advancedSearchQuery;
    }

    // Convert grid filters to advanced query and merge
    if (hasGridFilters) {
      const gridQuery = gridFilterToAdvancedQuery(
        gridFilterModel,
        auditLogsFieldConfig
      );

      if (gridQuery.filterGroups.length > 0) {
        finalAdvancedQuery = mergeAdvancedQueries(
          finalAdvancedQuery,
          gridQuery
        );
      }
    }

    // Check if there are actual filter rules
    const hasActualFilters = finalAdvancedQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );

    // Use advanced search if we have grid filters OR advanced search mode
    // Only use basic search when in basic mode
    return {
      search:
        searchMode === 'basic'
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch: hasActualFilters ? finalAdvancedQuery : undefined,
      sortBy:
        gridSortModel.length > 0
          ? (gridSortModel[0].field as keyof AuditLog)
          : undefined,
      sortDirection:
        gridSortModel.length > 0
          ? (gridSortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC')
          : undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };
  }, [
    searchMode,
    basicSearchTerm,
    advancedSearchQuery,
    gridFilterModel,
    gridPaginationModel,
    gridSortModel,
  ]);

  const {
    data: auditLogs,
    total,
    isLoading,
  } = useAuditLogsPage(auditLogsParams);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total && total > 0) {
      setStableTotal(total);
    }
  }, [total]);

  // DataGrid columns from adapter
  const auditLogsColumns = useMemo(() => toDataGridColumns(), []);

  // Reset page when search or filters change
  useEffect(() => {
    setGridPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchMode, basicSearchTerm, advancedSearchQuery, gridFilterModel]);

  return (
    <div style={styles.pageContainerStyle}>
      <div style={styles.searchBarContainerStyle}>
        <div style={styles.searchContainerStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={setBasicSearchTerm}
            onBasicSearchClear={() => setBasicSearchTerm('')}
            basicPlaceholder="Search audit logs..."
            advancedFieldConfigs={auditLogsFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={(query: AdvancedSearchQuery) => {
              setAdvancedSearchQuery(query);
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
            }}
          />
        </div>
      </div>

      <div style={styles.tableContainerStyle}>
        <LgmDataGrid<AuditLog>
          data={auditLogs}
          columns={auditLogsColumns}
          loading={isLoading}
          rowCount={stableTotal}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.audit_id}
          // No edit or delete capabilities for audit logs
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
  searchContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  stickyTopBarStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
};

export default AuditLogs;
